/**
 * messageRowIdentity — stable React row keys + entry-animation gating for the
 * transcript (owner UX directive, 2026-08-22: "messages fade in and out
 * rapidly… should be a smooth, modern experience").
 *
 * The flash had three compounding causes, and this module addresses the two
 * that are data-shaped:
 *
 * 1. Rows were keyed by ARRAY INDEX (`msg-${messageIndex}`) while the two
 *    store merges shift the index space under them — the dual-store merge
 *    timestamp-INSERTS foreign rows (interruptedTurn.ts splice) and the
 *    group merge fully re-sorts on every snapshot. Index keys + shifting
 *    content = React re-rendering rows as different elements; where the row
 *    also changes lane (bubble ↔ tile ↔ widget ↔ older/recent array), a full
 *    unmount/remount.
 *
 * 2. The bubble carried an UNCONDITIONAL 300ms fade+rise animation, so every
 *    remount replayed it from opacity 0 — the visible "fade in and out".
 *
 * `stableMessageRowKey` keys a row on the identity that SURVIVES convergence:
 * a local pending bubble carries `serverTaskId` before the server copy
 * exists, and the converged copy's `taskAnchorId`/id is exactly
 * `prest0n_task_<serverTaskId>` — normalizing the local form onto that anchor
 * means the key does not change when the merge swaps the row's id from the
 * client uuid to the server anchor. Rows without task identity (user bubbles,
 * agent notes) keep their own id, which both merges preserve per-viewer.
 *
 * `shouldAnimateMessageEntry` allows the entry animation only for rows whose
 * own timestamp says they just arrived — a remount minutes later (older/
 * recent boundary reparenting, list churn) renders settled and still.
 */

export interface RowIdentityMessage {
  id?: string;
  taskAnchorId?: string;
  serverTaskId?: string;
}

export function stableMessageRowKey(
  m: RowIdentityMessage,
  index: number,
  seen: Set<string>,
): string {
  const anchor = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
  const serverTaskId = typeof m.serverTaskId === 'string' ? m.serverTaskId.trim() : '';
  const id = m.id !== undefined && m.id !== null ? String(m.id).trim() : '';
  const base = anchor
    || (serverTaskId ? `prest0n_task_${serverTaskId}` : '')
    || id
    || `idx:${index}`;
  // Duplicate belt: two rows legitimately sharing an anchor (rare inline
  // pending + reply overlap) must not produce duplicate React keys.
  let key = base;
  let bump = 1;
  while (seen.has(key)) {
    key = `${base}#${bump}`;
    bump += 1;
  }
  seen.add(key);
  return key;
}

/** Entry animation plays only for a row whose own stamp says it JUST arrived.
 *  Small negative tolerance absorbs client↔server clock skew. */
const ENTRY_ANIMATION_MAX_AGE_MS = 4_000;
const ENTRY_ANIMATION_SKEW_TOLERANCE_MS = 60_000;

export function shouldAnimateMessageEntry(
  m: { orderTs?: number; timestamp?: string | number | Date },
  nowMs: number,
): boolean {
  const fromOrder = Number(m.orderTs);
  let ts = Number.isFinite(fromOrder) && fromOrder > 0 ? fromOrder : 0;
  if (!ts && m.timestamp !== undefined && m.timestamp !== null) {
    const raw = m.timestamp instanceof Date
      ? m.timestamp.getTime()
      : typeof m.timestamp === 'number'
        ? m.timestamp
        : Date.parse(String(m.timestamp));
    ts = Number.isFinite(raw) && raw > 0 ? raw : 0;
  }
  if (!ts) return false; // unknown age → render settled, never flash
  const age = nowMs - ts;
  return age >= -ENTRY_ANIMATION_SKEW_TOLERANCE_MS && age <= ENTRY_ANIMATION_MAX_AGE_MS;
}
